import { computed, ref } from 'vue';
import { defineStore } from 'pinia';
import { bonusAPI } from '@/services/api';
import { useAuthStore } from '@/stores/auth';
import { useGamesStore } from '@/stores/games';
import { useNotificationStore } from '@/stores/notifications';
import { t } from '@/i18n';

const LIVE_STATUSES = new Set(['ASSIGNED', 'PENDING', 'ACTIVE']);
const FINAL_STATUSES = new Set(['COMPLETED', 'CANCELLED', 'EXPIRED']);

const asNumber = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const asArray = (value) => (Array.isArray(value) ? value.filter(Boolean) : []);

const normalizeStatus = (value) => String(value || '').trim().toUpperCase();
const normalizeType = (value) => String(value || '').trim().toUpperCase();

const normalizeBonus = (bonus = {}) => {
  const id = String(bonus.id || bonus._id || '').trim();
  const type = normalizeType(bonus.type);
  const status = normalizeStatus(bonus.status);
  return {
    ...bonus,
    id,
    _id: id,
    type,
    status,
    templateName: String(bonus.templateName || bonus.templateCode || type || 'Bono').trim(),
    currency: String(bonus.currency || 'ARS').trim() || 'ARS',
    bonusAmount: asNumber(bonus.bonusAmount, 0),
    bonusBalance: asNumber(bonus.bonusBalance, 0),
    lockedBonusBalance: asNumber(bonus.lockedBonusBalance, 0),
    wagerTotal: asNumber(bonus.wagerTotal, 0),
    wagerRemaining: asNumber(bonus.wagerRemaining, 0),
    freespinsTotal: asNumber(bonus.freespinsTotal, 0),
    freespinsRemaining: asNumber(bonus.freespinsRemaining, 0),
    discountCap: asNumber(bonus.discountCap, 0),
    discountUsed: asNumber(bonus.discountUsed, 0),
    OPTIN: Boolean(bonus.OPTIN),
    accepted: Boolean(bonus.accepted),
    eligibleGameIds: asArray(bonus.eligibleGameIds),
    eligibleProviderIds: asArray(bonus.eligibleProviderIds),
    termsAndConditions: String(bonus.termsAndConditions || '').trim(),
    wageringRules: bonus.wageringRules || null,
    wageringProgressPct: asNumber(bonus.wageringProgressPct, 0),
    convertibleBalance: asNumber(bonus.convertibleBalance, 0),
  };
};

const isClaimableBonus = (bonus) => (
  bonus.status === 'ASSIGNED'
  && bonus.OPTIN === true
  && bonus.accepted !== true
);

export const useBonusStore = defineStore('bonus', () => {
  const bonuses = ref([]);
  const total = ref(0);
  const loading = ref(false);
  const acceptingId = ref('');
  const error = ref(null);
  const loadedAt = ref(null);

  const claimableBonuses = computed(() => bonuses.value.filter(isClaimableBonus));
  const liveBonuses = computed(() => bonuses.value.filter((bonus) => LIVE_STATUSES.has(bonus.status) && !isClaimableBonus(bonus)));
  const finishedBonuses = computed(() => bonuses.value.filter((bonus) => FINAL_STATUSES.has(bonus.status)));
  const recentBonuses = computed(() => bonuses.value.slice(0, 5));
  const claimableCount = computed(() => claimableBonuses.value.length);
  const hasBonuses = computed(() => bonuses.value.length > 0);

  const clearError = () => {
    error.value = null;
  };

  const reset = () => {
    bonuses.value = [];
    total.value = 0;
    loading.value = false;
    acceptingId.value = '';
    error.value = null;
    loadedAt.value = null;
  };

  const fetchBonuses = async ({ force = false } = {}) => {
    const authStore = useAuthStore();
    if (!authStore.isAuthenticated) {
      reset();
      return { success: false, error: t('errors.auth.sessionNotFound') };
    }

    if (loading.value && !force) {
      return { success: true, data: { bonuses: bonuses.value, total: total.value } };
    }

    loading.value = true;
    clearError();

    const response = await bonusAPI.listBonuses({ limit: 100 });
    loading.value = false;

    if (!response.success) {
      error.value = response.error.message;
      return { success: false, error: response.error.message };
    }

    const nextBonuses = Array.isArray(response.data?.bonuses)
      ? response.data.bonuses.map(normalizeBonus).filter((bonus) => bonus.id)
      : [];

    bonuses.value = nextBonuses;
    total.value = asNumber(response.data?.total, nextBonuses.length);
    loadedAt.value = new Date().toISOString();

    return {
      success: true,
      data: {
        bonuses: bonuses.value,
        total: total.value,
      },
    };
  };

  const refreshBalances = async () => {
    const authStore = useAuthStore();
    const gamesStore = useGamesStore();
    await Promise.allSettled([
      authStore.refreshBalance(),
      gamesStore.fetchBalance(),
    ]);
  };

  const acceptBonus = async (bonusId) => {
    const normalizedBonusId = String(bonusId || '').trim();
    if (!normalizedBonusId) {
      return { success: false, error: t('bonuses.invalid') };
    }

    acceptingId.value = normalizedBonusId;
    clearError();

    const txid = `accept-${normalizedBonusId}-${Date.now()}`;
    const response = await bonusAPI.acceptBonus({
      bonusId: normalizedBonusId,
      txid,
    });

    acceptingId.value = '';

    if (!response.success) {
      error.value = response.error.message;
      useNotificationStore().error(response.error.message || t('bonuses.activateFailed'), t('bonuses.notActivatedTitle'));
      return { success: false, error: response.error.message };
    }

    const acceptedBonus = normalizeBonus(response.data?.bonus || {});
    useNotificationStore().success(t('bonuses.activatedMessage', { bonus: acceptedBonus.templateName || t('bonuses.defaultName') }), t('bonuses.activatedTitle'));

    await fetchBonuses({ force: true });
    await refreshBalances();

    return {
      success: true,
      data: acceptedBonus,
    };
  };

  return {
    bonuses,
    total,
    loading,
    acceptingId,
    error,
    loadedAt,
    claimableBonuses,
    liveBonuses,
    finishedBonuses,
    recentBonuses,
    claimableCount,
    hasBonuses,
    fetchBonuses,
    acceptBonus,
    reset,
    clearError,
  };
});
